// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Stethoscope, HeartHandshake } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/use-auth";
import { Badge } from "@/components/ui/badge";
import { useGetData } from "@/hooks/useGetData";
import { useState } from "react";

export default function PatientDashboard() {
  const { user, displayName, patientEmail } = useAuth();
  const [assignedCaretakers, setAssignedCaretakers] = useState([]);
  const [assignedProvider, setAssignedProviders] = useState({});

  const { apiData } = useGetData("/patients/dashboard", {}, false, true, (err, data)=> {
    if(data && !err){
      console.log(data)
      setAssignedProviders(data?.data?.patient?.assignedProviderId ?  data?.data?.patient?.assignedProviderId : []);
      setAssignedCaretakers(data?.data?.patient?.assignedCaretakerIds ? data?.data?.patient?.assignedCaretakerIds  : []);
    }
  });

  const patientFromApi = apiData?.data?.patient;
  const currentPatient = patientFromApi ? {
    firstName: patientFromApi.firstName,
    middleName: patientFromApi.middleName,
    lastName: patientFromApi.lastName,
    email: patientFromApi.email || patientEmail,
    assignedCaretakerIds: patientFromApi.assignedCaretakerIds,
    assignedProviderId: patientFromApi.assignedProviderId,
  } : null;

  const patientFullName = currentPatient
    ? [currentPatient.firstName, currentPatient.middleName, currentPatient.lastName].filter(Boolean).join(" ").trim()
    : "";

  // Example caretakers list
  const caretakers = [
    {
      _id: "c1",
      firstName: "Emily",
      lastName: "Stone",
      email: "emily.stone@healthcare.com",
      phone: "+91 9876543210",
      status: "Active",
    },
    {
      _id: "c2",
      firstName: "David",
      lastName: "Brown",
      email: "david.brown@healthcare.com",
      phone: "+91 9123456789",
      status: "Inactive",
    },
  ];

  // Example provider data
  const providers = [
    {
      _id: "p1",
      name: "Dr. Alice Carter",
      specialty: "Cardiology",
      licenseNumber: "LIC-98765",
      status: "Active",
    },
  ];
  console.log({assignedCaretakers, assignedProvider})

  // =============================
  // 🔹 Derived data (filtering)
  // =============================
  // const assignedCaretakers =
  //   caretakers.filter((c) =>
  //     currentPatient.assignedCaretakerIds?.includes(c._id)
  // ) || [];

  // const assignedProvider = providers.find(
  //   (p) => p._id === currentPatient.assignedProviderId
  // );

  // =============================
  // 🔹 UI
  // =============================
  if (!currentPatient) {
    return (
      <DashboardLayout activePage="/patient-dashboard">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activePage="/patient-dashboard">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Welcome, {(() => {
              const name = patientFullName || displayName || user?.name || "Patient";
              return typeof name === "string" ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : name;
            })()}
          </h2>
          <p className="text-muted-foreground mt-2">
            Your healthcare team and care information
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* My Caretakers Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HeartHandshake className="w-5 h-5" />
                  My Caretakers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {assignedCaretakers.length > 0 ? (
                  <div className="space-y-4">
                    {assignedCaretakers.map((caretaker, index) => (
                      <div key={caretaker._id} className={index > 0 ? "pt-4 border-t" : ""}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
                            {caretaker.firstName.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-lg">
                              {caretaker.firstName} {caretaker.lastName}
                            </p>
                            <Badge variant="outline" className="mt-1">
                              {caretaker.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <span>{caretaker.email}</span>
                          </div>
                          {/* <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            <span>{caretaker.phone}</span>
                          </div> */}
                        </div>

                        <Button className="w-full mt-3" variant="outline" size="sm" asChild>
                          <a href={`mailto:${caretaker.email}`}>
                            <Mail className="w-4 h-4 mr-2" />
                            Contact
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No caretakers assigned yet
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* My Providers Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="w-5 h-5" />
                  My Provider
                </CardTitle>
              </CardHeader>
              <CardContent>
                {assignedProvider ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
                        <Stethoscope className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">
                          {assignedProvider.firstName } {assignedProvider.lastName}
                        </p>
                        <Badge variant="outline" className="mt-1">
                          {assignedProvider.status}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="text-sm text-muted-foreground pt-2">
                      <p>NPI Number: {assignedProvider.npiNumber}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No provider assigned yet
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}